import { useEffect, useRef, useState } from "react";
import { ActionBar, Button, HStack, Separator, Text, VStack } from "@chakra-ui/react";
import { useAppStore } from "../../../context/store";
import { LuAlignCenterVertical, LuSquareFunction, LuUndo, LuX } from "react-icons/lu";
import Type from "../../../utilies/Types";
import { removeFileExtension, showToast } from "../../../utilies/Utility";
import { Tooltip } from "../../ui/tooltip";
import { useLaneletBuilder } from "../../../utilies/lanelet/Create";
import { useMapObjects } from "../../../context/MapObjectsContext";

function Lanelet() {
  // state
  const viewerRef = useAppStore((s) => s.viewerRef);
  const showLaneletButton = useAppStore((s) => s.showLaneletButton);
  const createLaneletMode = useAppStore((s) => s.createLaneletMode);
  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();
  const activeLayer = useAppStore((s) => s.activeLayer);
  const autoSelectRef = useAppStore((s) => s.autoSelectRef);
  const autoSelect = useAppStore((s) => s.autoSelect);
  const basicGeojsonStyle = useAppStore((s) => s.basicGeojsonStyle);
  const highlightToolsRef = useAppStore((s) => s.highlightToolsRef);
  const editMode = useAppStore((s) => s.editMode);
  const savedHotkeysRef = useAppStore((s) => s.savedHotkeysRef);
  const userCanEditing = useAppStore((s) => s.userCanEditing);
  const isActiveChecker = useAppStore((s) => s.isActiveChecker);
  const infoboxRef = useAppStore((s) => s.infoboxRef);
  const userData = useAppStore((s) => s.userData);

  const userType = useAppStore((s) => s.userType);

  // setters
  const setShowLaneletButton = useAppStore.getState().setShowLaneletButton;
  const setCreateLaneletMode = useAppStore.getState().setCreateLaneletMode;
  const createLaneletModeRef = useAppStore.getState().createLaneletModeRef;

  const setAutoSelect = useAppStore.getState().setAutoSelect;
  const setCenterLineLastFeature = useAppStore.getState().setCenterLineLastFeature;

  const setCanOpenInfobox = useAppStore.getState().setCanOpenInfobox;
  const setCanHightlightEntity = useAppStore.getState().setCanHightlightEntity;
  const setIsActiveChecker = useAppStore.getState().setIsActiveChecker;

  const { removeLastCenterline } = useLaneletBuilder({
    viewerRef,
    createLaneletMode,
    activeLayer,
    autoSelect,
    autoSelectRef,
    mapObjectsRef,
    setMapObjects,
    basicGeojsonStyle,
    setCenterLineLastFeature,
    setCanOpenInfobox,
    setCanHightlightEntity,
    highlightToolsRef,
    infoboxRef,
    userData,
    createLaneletModeRef,
  });

  const closeLaneletUi = () => {
    try {
      setIsActiveChecker(false);
    } catch {}

    try {
      setCanOpenInfobox(true);
      setCanHightlightEntity(true);
    } catch {}

    try {
      const popup = document.getElementById("map-popup-form");
      if (popup) popup.remove();
    } catch {}

    try {
      if (window.__mapPopup_onSpacePress) {
        window.removeEventListener("keydown", window.__mapPopup_onSpacePress);
        delete window.__mapPopup_onSpacePress;
      }
    } catch {}

    setCreateLaneletMode(false);
  };

  // ESC: exit lanelet mode + full reset
  useEffect(() => {
    if (!createLaneletMode && !isActiveChecker) return;

    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      closeLaneletUi();
    };

    window.addEventListener("keydown", onEsc, { capture: true });
    return () => window.removeEventListener("keydown", onEsc, { capture: true });
  }, [createLaneletMode, isActiveChecker]);

  // Keyboard events
  useEffect(() => {
    const isMod = (k) => ["shift", "ctrl", "alt", "meta"].includes(String(k).toLowerCase());
    const isAlpha = (k) => /^[a-z]$/i.test(String(k || ""));
    const normalizeKey = (k) => {
      let key = String(k || "").toLowerCase();
      if (key === "control") key = "ctrl";
      if (key === " ") key = "space";
      return key;
    };

    const getCombo = (type) => savedHotkeysRef.current.find((h) => h.type === type)?.combination || [];

    const matches = (e, combo) => {
      if (!combo || combo.length === 0) return false;

      const norm = combo.map(normalizeKey);

      const want = {
        shift: norm.includes("shift"),
        ctrl: norm.includes("ctrl"),
        alt: norm.includes("alt"),
        meta: norm.includes("meta"),
      };

      if (e.shiftKey !== want.shift) return false;
      if (e.ctrlKey !== want.ctrl) return false;
      if (e.altKey !== want.alt) return false;
      if (e.metaKey !== want.meta) return false;

      const main = norm.find((k) => !isMod(k));
      if (!main) return true;

      const pressed = normalizeKey(e.key);
      return pressed === main;
    };

    // -------------------------
    // 3-key sequential support
    // combo: [mod, letter1, letter2]
    // user: mod+letter1, then within 600ms press letter2 (with same mods or no mods)
    // -------------------------
    const PENDING_KEY = "__create_lanelet_hotkey_pending__";
    if (!window[PENDING_KEY]) {
      window[PENDING_KEY] = {
        active: false,
        mods: [],
        stepKey: "",
        finalKey: "",
        expiresAt: 0,
        tid: null,
      };
    }
    const pending = window[PENDING_KEY];

    const resetPending = () => {
      if (pending.tid) clearTimeout(pending.tid);
      pending.active = false;
      pending.mods = [];
      pending.stepKey = "";
      pending.finalKey = "";
      pending.expiresAt = 0;
      pending.tid = null;
    };

    const modsMatchExactly = (e, mods) => {
      const needShift = mods.includes("shift");
      const needCtrl = mods.includes("ctrl");
      const needAlt = mods.includes("alt");
      const needMeta = mods.includes("meta");

      if (e.shiftKey !== needShift) return false;
      if (e.ctrlKey !== needCtrl) return false;
      if (e.altKey !== needAlt) return false;
      if (e.metaKey !== needMeta) return false;
      return true;
    };

    const modsAreNone = (e) => !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey;

    const isSequential3Combo = (combo) => {
      if (!combo || combo.length !== 3) return false;

      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));

      return mods.length === 1 && nonMods.length === 2 && isAlpha(nonMods[0]) && isAlpha(nonMods[1]);
    };

    const getSequentialParts = (combo) => {
      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));
      return { mods, stepKey: nonMods[0], finalKey: nonMods[1] };
    };

    const startPending = (mods, stepKey, finalKey) => {
      resetPending();
      pending.active = true;
      pending.mods = mods;
      pending.stepKey = stepKey;
      pending.finalKey = finalKey;
      pending.expiresAt = Date.now() + 3000;
      pending.tid = setTimeout(() => resetPending(), 3000);
    };

    const handleKeyDown = (event) => {
      const combo = getCombo(Type.HOTKEYS.CREATE_LANELET);

      // 1) pending final key check
      if (pending.active) {
        if (Date.now() > pending.expiresAt) {
          resetPending();
        } else {
          const k = normalizeKey(event.key);
          const okMods = modsMatchExactly(event, pending.mods) || modsAreNone(event);

          if (okMods && k === normalizeKey(pending.finalKey)) {
            event.preventDefault();
            resetPending();

            if (!(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN))) return;

            const newVal = !createLaneletMode;
            setCreateLaneletMode(newVal);
            setCanHightlightEntity(!newVal);
            return;
          }

          if (!isMod(k)) resetPending();
        }
      }

      // 2) sequential start
      if (isSequential3Combo(combo)) {
        const { mods, stepKey, finalKey } = getSequentialParts(combo);

        if (modsMatchExactly(event, mods) && normalizeKey(event.key) === normalizeKey(stepKey)) {
          event.preventDefault();
          startPending(mods, stepKey, finalKey);
          return;
        }
      }

      // 3) normal one-shot
      if (matches(event, combo)) {
        event.preventDefault();
        resetPending();

        if (!(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN))) return;

        const newVal = !createLaneletMode;
        setCreateLaneletMode(newVal);
        setCanHightlightEntity(!newVal);
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      resetPending();
    };
  }, [activeLayer, mapObjects, viewerRef, createLaneletMode]);

  // Show/hide lanelet buttons
  useEffect(() => {
    let isShow = false;

    if (activeLayer != null) {
      for (const key in mapObjects) {
        if (mapObjects[key].id == activeLayer) {
          const element = mapObjects[key];
          if (removeFileExtension(element.file_name).toLowerCase().includes("lanelet")) {
            if (element.visible) {
              isShow = true;
              break;
            }
          }
        }
      }
    }

    setShowLaneletButton(isShow);
    if (!isShow) {
      closeLaneletUi();
    }
  }, [activeLayer, mapObjects]);

  if (!showLaneletButton || !userCanEditing) return null;

  const mo = mapObjectsRef.current || [];
  const layerId = activeLayer;
  const targetLayer = mo.find((layer) => layer.id === layerId);
  const featureCount = targetLayer?.dataSource?.entities?.values?.length ?? targetLayer?.dataSource?._entityCollection?.values?.length ?? 0;
  const hasFeatures = featureCount > 0;

  return (
    <>
      <HStack>
        {/* Lanelet mode toggle (main button only) */}
        <Button
          disabled={editMode}
          variant={"solid"}
          p={0}
          opacity={1}
          bg={createLaneletMode ? "green" : "white"}
          color={createLaneletMode ? "white" : "MenuText"}
          colorPalette={createLaneletMode ? "green" : "white"}
          onClick={() => {
            if (createLaneletMode) {
              closeLaneletUi();
              return;
            }

            const newVal = !createLaneletMode;
            setCreateLaneletMode(newVal);
            setCanHightlightEntity(!newVal);
          }}
        >
          <LuAlignCenterVertical size={20} />
        </Button>
      </HStack>

      <ActionBar.Root open={createLaneletMode} closeOnEscape={false} closeOnInteractOutside={false}>
        <ActionBar.Positioner>
          <ActionBar.Content>
            <VStack gap={0} align="flex-start">
              <Text fontWeight="semibold" fontSize="sm">
                Lanelet Tool
              </Text>

              <Separator my={2} width={"full"} />

              <HStack>
                {/* Undo last centerline */}
                {hasFeatures ? (
                  <Button
                    disabled={editMode}
                    variant={"outline"}
                    p={0}
                    opacity={1}
                    bg="white"
                    color="MenuText"
                    colorPalette="white"
                    onClick={() => {
                      removeLastCenterline();
                    }}
                  >
                    <Tooltip content={"Undo"}>
                      <LuUndo size={20} />
                    </Tooltip>
                  </Button>
                ) : null}

                {/* Auto-select toggle */}
                <Button
                  disabled={editMode}
                  variant={"outline"}
                  p={0}
                  opacity={1}
                  bg={autoSelect ? "green" : "white"}
                  color={autoSelect ? "white" : "MenuText"}
                  colorPalette={autoSelect ? "green" : "white"}
                  onClick={() => {
                    const newVal = !autoSelect;
                    setAutoSelect(newVal);
                    if (newVal) {
                      showToast("Auto-selection mode is now enabled", Type.NOTIFICATION.INFO);
                    } else {
                      showToast("Auto-selection mode is now disabled", Type.NOTIFICATION.INFO);
                    }
                  }}
                >
                  <Tooltip content={"Auto Select"}>
                    <LuSquareFunction size={20} />
                  </Tooltip>
                </Button>

                {/* Disable lanelet tool */}
                <Button
                  disabled={editMode}
                  variant={"outline"}
                  p={0}
                  opacity={1}
                  bg="white"
                  color="red.500"
                  colorPalette="red"
                  onClick={() => {
                    closeLaneletUi();
                  }}
                >
                  <Tooltip content={"Close"}>
                    <LuX size={20} />
                  </Tooltip>
                </Button>
              </HStack>
            </VStack>
          </ActionBar.Content>
        </ActionBar.Positioner>
      </ActionBar.Root>
    </>
  );
}

export default Lanelet;
